/**
 * Offener Bon des Verkaufs in der Browser-Sitzung (sessionStorage): Ein Wechsel zu
 * „Belege“, „Termine“ oder „Lager“ baut den Verkauf ab – ohne Sicherung wären ein
 * aufgerufener Tisch (am Server schon gelöscht), Gutscheine am Bon oder eine offene
 * Kartenzahlung verloren. Kein Beleg: der entsteht erst beim Kassieren.
 */
import type { Speicher } from './belegnummern';

export interface GesicherterBon {
  positionen: unknown[];
  aktiverTisch: string | null;
  notiz: string;
  boniert: unknown[];
  empfaenger: { name: string; anschrift: string; uid: string };
  kundeEmail: string | null;
  gutscheinAusgaben: unknown[];
  einloesungen: unknown[];
  offeneZahlung: unknown;
  /** Positionen, über die die offene Kartenzahlung läuft (beim Teilen nur ein Teil des Bons). */
  zahlungPositionen: unknown[] | null;
  /** Kartenzahlung gestartet, aber die Antwort ging verloren: ab hier (ISO) nach ihr suchen. */
  startUnklarSeit: string | null;
  /** P13: Vorgangs-ID des unklaren Kartenzahlungsstarts (exakte Klärung über die API). */
  startVorgang?: string | null;
  /** Beleg gesendet, Antwort steht aus – nach Neuladen klären, sonst droht ein Doppelbeleg. */
  offenerBeleg: {
    belegnummer: string;
    positionen: unknown[];
    vorgangId?: string;
    /** Codex-Prüfung 4: aus einem anderen Fenster zum Klären übernommen – nicht der Bon hier */
    uebernommen?: true;
  } | null;
  /** Übernommener Kunde und sein Rabatt (Sicherheitspaket S3: der Server prüft den Rabatt am Kunden). */
  kunde?: { id: number; rabattProzent: number | null } | null;
  /**
   * Codex-Prüfung 4: Kassierreservierung, die dieser Bon trägt (Kartenstart, Lieferdienst). Ihr
   * dauerhaft festgehaltener Ausgang entscheidet, ob der Bon schon verkauft ist – auch nach
   * Neuladen, Browserneustart oder in einem duplizierten Fenster.
   */
  reservierung?: { art: string; id: string } | null;
  /**
   * Codex-Prüfung 4: Reservierung eines anderen Fensters, die dieses Fenster nur klärt (Zahlung
   * prüfen, Erfassung prüfen) – ihr Einkauf ist nicht der Bon dieses Fensters.
   */
  uebernommen?: { art: string; id: string } | null;
}

function schluessel(kassenId: string): string {
  return `bilanx.bon.${kassenId}`;
}

function vorgangSchluessel(kassenId: string): string {
  return `bilanx.vorgang.${kassenId}`;
}

/**
 * Offline-Plan P1-13 (Codex C2): Ungeklärte Kartenzahlung bzw. Belegerstellung – Zahlung mit
 * Referenz, unklarer Start mit Vorgangs-ID, gesendeter Beleg ohne Antwort – liegt zusätzlich
 * dauerhaft (localStorage), damit die Schutzsperre und „Zahlung prüfen“/„Beleg prüfen“ auch eine
 * neue Browsersitzung (Neustart, Absturz) überleben. Nur diese Teile, nicht der ganze Bon
 * (kein Empfänger, keine E-Mail-Adresse); aufgeräumt, sobald alles geklärt ist.
 */
/**
 * Codex 3: Von einer offenen Zahlung nur ausdrücklich freigegebene Felder – Referenz, Kasse,
 * Anbieter, Betrag, Trinkgeld, Status. Nie `details` (Terminalbelegzeilen, maskierte
 * Kartendaten, Rohdaten des Anbieters). „Zahlung prüfen“ holt den vollen Stand vom Server.
 */
export function zahlungZumSichern(z: unknown): Record<string, unknown> | null {
  if (typeof z !== 'object' || z === null) {
    return null;
  }
  const o = z as Record<string, unknown>;
  if (typeof o.zahlungsreferenz !== 'string') {
    return null;
  }
  return {
    zahlungsreferenz: o.zahlungsreferenz,
    ...(typeof o.kassenId === 'string' ? { kassenId: o.kassenId } : {}),
    ...(typeof o.anbieter === 'string' ? { anbieter: o.anbieter } : {}),
    ...(Number.isInteger(o.betragCent) ? { betragCent: o.betragCent } : {}),
    ...(Number.isInteger(o.trinkgeldCent) ? { trinkgeldCent: o.trinkgeldCent } : {}),
    ...(typeof o.status === 'string' ? { status: o.status } : {}),
    details: {},
  };
}

/**
 * Eigener offener Teil des Bons für die dauerhafte Sicherung (P1-13). Codex-Prüfung 4: Was dieses
 * Fenster nur für ein anderes klärt (übernommene Reservierung, übernommener Vorgang), gehört nicht
 * dazu – sonst stellte ein Neustart einen fremden Einkauf als eigenen Bon wieder her.
 */
export function eigenerVorgangsteil(bon: GesicherterBon): Record<string, unknown> | null {
  const karteFremd = bon.uebernommen?.art === 'karte';
  const offeneZahlung = karteFremd ? null : bon.offeneZahlung;
  const startUnklarSeit = karteFremd ? null : bon.startUnklarSeit;
  const offenerBeleg = bon.offenerBeleg?.uebernommen === true ? null : bon.offenerBeleg;
  if (
    offeneZahlung === null &&
    startUnklarSeit === null &&
    offenerBeleg === null &&
    bon.reservierung == null
  ) {
    return null;
  }
  return {
    offeneZahlung: zahlungZumSichern(offeneZahlung),
    zahlungPositionen: karteFremd ? null : bon.zahlungPositionen,
    startUnklarSeit,
    ...(karteFremd || bon.startVorgang == null ? {} : { startVorgang: bon.startVorgang }),
    offenerBeleg,
    ...(bon.reservierung == null ? {} : { reservierung: bon.reservierung }),
  };
}

/** Dauerhaft gesicherter offener Vorgang als (minimaler) Bon für `ladeBon`. */
function vorgangAlsBon(roh: string | null): string | null {
  if (roh === null || roh === '') {
    return null;
  }
  try {
    const v = JSON.parse(roh) as Record<string, unknown>;
    const ob = v.offenerBeleg as { positionen?: unknown } | null | undefined;
    const positionen = Array.isArray(v.zahlungPositionen)
      ? v.zahlungPositionen
      : Array.isArray(ob?.positionen)
        ? ob.positionen
        : [];
    return JSON.stringify({ ...v, positionen, aktiverTisch: null, notiz: '', boniert: [] });
  } catch {
    return null;
  }
}

/**
 * Bon der Sitzung laden. Ohne Sitzungsstand (neue Browsersitzung) mit `dauerhaft` den dort
 * gesicherten ungeklärten Vorgang (P1-13) – die Sperre gilt dann weiter bis zur Klärung.
 */
export function ladeBon(
  speicher: Speicher,
  kassenId: string,
  dauerhaft?: Speicher,
): GesicherterBon | null {
  let roh = speicher.getItem(schluessel(kassenId));
  if ((roh === null || roh === '') && dauerhaft !== undefined) {
    roh = vorgangAlsBon(dauerhaft.getItem(vorgangSchluessel(kassenId)));
  }
  if (roh === null || roh === '') {
    return null;
  }
  try {
    const b = JSON.parse(roh) as Partial<GesicherterBon>;
    const e = b.empfaenger;
    if (!Array.isArray(b.positionen)) {
      return null;
    }
    return {
      positionen: b.positionen,
      aktiverTisch: typeof b.aktiverTisch === 'string' ? b.aktiverTisch : null,
      notiz: typeof b.notiz === 'string' ? b.notiz : '',
      boniert: Array.isArray(b.boniert) ? b.boniert : [],
      empfaenger:
        typeof e === 'object' &&
        typeof e.name === 'string' &&
        typeof e.anschrift === 'string' &&
        typeof e.uid === 'string'
          ? e
          : { name: '', anschrift: '', uid: '' },
      kundeEmail: typeof b.kundeEmail === 'string' ? b.kundeEmail : null,
      gutscheinAusgaben: Array.isArray(b.gutscheinAusgaben) ? b.gutscheinAusgaben : [],
      einloesungen: Array.isArray(b.einloesungen) ? b.einloesungen : [],
      offeneZahlung: b.offeneZahlung ?? null,
      zahlungPositionen: Array.isArray(b.zahlungPositionen) ? b.zahlungPositionen : null,
      startUnklarSeit: typeof b.startUnklarSeit === 'string' ? b.startUnklarSeit : null,
      ...(typeof b.startVorgang === 'string' && /^[A-Za-z0-9-]{8,100}$/.test(b.startVorgang)
        ? { startVorgang: b.startVorgang }
        : {}),
      offenerBeleg:
        typeof b.offenerBeleg === 'object' &&
        b.offenerBeleg !== null &&
        typeof b.offenerBeleg.belegnummer === 'string' &&
        Array.isArray(b.offenerBeleg.positionen)
          ? b.offenerBeleg
          : null,
      // nur gesetzt, wenn ein Kunde gesichert ist (ältere Sicherungen bleiben unverändert)
      ...(typeof b.reservierung === 'object' &&
      b.reservierung !== null &&
      typeof b.reservierung.art === 'string' &&
      typeof b.reservierung.id === 'string'
        ? { reservierung: { art: b.reservierung.art, id: b.reservierung.id } }
        : {}),
      ...(typeof b.uebernommen === 'object' &&
      b.uebernommen !== null &&
      typeof b.uebernommen.art === 'string' &&
      typeof b.uebernommen.id === 'string'
        ? { uebernommen: { art: b.uebernommen.art, id: b.uebernommen.id } }
        : {}),
      ...(typeof b.kunde === 'object' &&
      b.kunde !== null &&
      Number.isInteger(b.kunde.id) &&
      (b.kunde.rabattProzent === null || typeof b.kunde.rabattProzent === 'number')
        ? { kunde: { id: b.kunde.id, rabattProzent: b.kunde.rabattProzent } }
        : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Leerer Bon ohne offene Zahlung wird entfernt statt gespeichert. Mit `dauerhaft` wird ein
 * ungeklärter Vorgang zusätzlich dort gesichert bzw. nach der Klärung entfernt (P1-13).
 * Ergebnis: false, wenn das dauerhafte Sichern bzw. Bereinigen scheiterte (Codex 7: melden).
 */
export function speichereBon(
  speicher: Speicher,
  kassenId: string,
  bon: GesicherterBon,
  dauerhaft?: Speicher,
): boolean {
  let dauerhaftOk = true;
  if (dauerhaft !== undefined) {
    const teil = eigenerVorgangsteil(bon);
    try {
      dauerhaft.setItem(vorgangSchluessel(kassenId), teil === null ? '' : JSON.stringify(teil));
    } catch {
      // Speicher voll: die Sitzungssicherung bleibt wie bisher; der Aufrufer meldet es
      dauerhaftOk = false;
    }
  }
  const leer =
    bon.positionen.length === 0 &&
    bon.aktiverTisch === null &&
    bon.boniert.length === 0 &&
    bon.einloesungen.length === 0 &&
    bon.offeneZahlung === null &&
    bon.startUnklarSeit === null &&
    bon.offenerBeleg === null;
  speicher.setItem(schluessel(kassenId), leer ? '' : JSON.stringify(bon));
  return dauerhaftOk;
}

/**
 * P1-13: Speicher für den Kartenstart-Merker – liest zuerst die Sitzung, sonst den dauerhaften
 * Speicher (neue Sitzung), und schreibt in beide. So gilt ein Start, der beim Neustart des
 * Browsers noch unterwegs war, weiterhin als unklar.
 */
export function sitzungMitDauerhaft(
  sitzung: Speicher,
  dauerhaft: Speicher,
  /** Codex 7: Ergebnis jedes dauerhaften Schreibens (false = gescheitert, melden). */
  ergebnis?: (ok: boolean) => void,
): Speicher {
  return {
    getItem: (k) => sitzung.getItem(k) ?? dauerhaft.getItem(k),
    setItem: (k, v) => {
      sitzung.setItem(k, v);
      try {
        dauerhaft.setItem(k, v);
        ergebnis?.(true);
      } catch {
        // Speicher voll: die Sitzung hält den Merker wie bisher
        ergebnis?.(false);
      }
    },
  };
}

function kartenstartSchluessel(kassenId: string): string {
  return `bilanx.kartenstart.${kassenId}`;
}

/**
 * Sicherheitspaket S2: Kartenzahlungsstart unterwegs – vor dem Aufruf gemerkt, nach der
 * Antwort gelöscht. Wird die Kasse währenddessen neu geladen, gilt der Start als unklar
 * („Zahlung prüfen“), statt dass derselbe Bon ein zweites Mal am Terminal startet.
 * Eigener Schlüssel, damit die laufende Bonsicherung den Merker nicht überschreibt.
 */
export function merkeKartenstart(
  speicher: Speicher,
  kassenId: string,
  seit: string | null,
  vorgangId: string | null = null,
): void {
  speicher.setItem(kartenstartSchluessel(kassenId), seit ?? '');
  speicher.setItem(
    `${kartenstartSchluessel(kassenId)}.vorgang`,
    seit === null ? '' : (vorgangId ?? ''),
  );
}

/** P13: Vorgangs-ID des gemerkten Kartenstarts oder null. */
export function ladeKartenstartVorgang(speicher: Speicher, kassenId: string): string | null {
  const v = speicher.getItem(`${kartenstartSchluessel(kassenId)}.vorgang`);
  return v === null || v === '' ? null : v;
}

export function ladeKartenstart(speicher: Speicher, kassenId: string): string | null {
  const seit = speicher.getItem(kartenstartSchluessel(kassenId));
  return seit === null || seit === '' ? null : seit;
}
